import { RiArrowLeftSLine } from '@remixicon/react';
import {
    ClipboardEvent,
    ComponentProps,
    useEffect,
    useRef,
    useState,
} from 'react';
import { INSTANCE_ID } from '~/constants';
import { getClientLogoImageWithText } from '~/helpers/image';
import { useTranslation } from 'react-i18next';
import { LogoWithText } from '~/components/logo';
import { Toast } from '~/components/toast';

interface LoginTwoFactorProps {
    userID: number;
    sms?: boolean;
    email?: boolean;
    user?: any;
    goBackCallback: Function;
    onSuccessCallback: Function;
}

interface FetchResult {
    error: number;
    title: string;
    message: string;
    timestamp?: number;
    type?: ComponentProps<typeof Toast>['type'];
}

export default function LoginTwoFactor({
    userID,
    sms,
    email,
    user,
    goBackCallback,
    onSuccessCallback,
}: LoginTwoFactorProps) {
    const { t } = useTranslation();

    const isClientLogo = [137, 143, 184, 232].includes(INSTANCE_ID);

    const timeToExpiration = 120; // default expiration time: 120seg
    const [loading, setLoading] = useState(false);
    const [timeToExpirationDate, setTimeToExpirationDate] =
        useState(timeToExpiration);
    const [minuteTimer, setMinuteTimer] = useState(0);
    const [secondTimer, setSecondTImer] = useState(0);
    const [timestamp, setTimestamp] = useState(0);

    const inputsRef = useRef<HTMLInputElement[]>([]);
    const intervalRef = useRef<NodeJS.Timeout>();

    const [fetchResult, setFetchResult] = useState<FetchResult>({
        error: 0,
        title: '',
        message: '',
    });

    const [openErrorToast, setOpenErrorToast] = useState<boolean>(false);
    const onToastClose = () => setOpenErrorToast(false);

    useEffect(() => {
        if (fetchResult.error < 0) {
            setOpenErrorToast(true);
        }
    }, [fetchResult]);

    useEffect(() => {
        // se estiver fechado nao corre o effect
        if (!openErrorToast) return undefined;
        // se estiver aberto corre um timeout e fecha o toast passado 6s
        const timeoutId = setTimeout(() => setOpenErrorToast(false), 6000);
        return () => clearTimeout(timeoutId);
    }, [openErrorToast]);

    useEffect(() => {
        const m = Math.floor((timeToExpirationDate % 3600) / 60);
        const s = Math.floor((timeToExpirationDate % 3600) % 60);
        setMinuteTimer(m);
        setSecondTImer(s);

        if (timeToExpirationDate <= 0) {
            clearInterval(intervalRef.current);
        }
    }, [timeToExpirationDate]);

    useEffect(() => {
        // Foca o primeiro input assim que o componente montar
        if (inputsRef.current[0]) {
            inputsRef.current[0].focus();
        }
    }, []);

    const handleKeyUpEvent = (current: number, prev: number, next: number) => {
        if (inputsRef.current[current].value.length === 0) {
            inputsRef.current[prev].focus();
        } else {
            inputsRef.current[next].focus();
        }
    };

    const handleInputFilter = (e: React.FormEvent<HTMLInputElement>) => {
        e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
    };

    const handlePasteEvent = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const digits = e.clipboardData.getData('text').replace(/\D/g, '');

        for (let index = 0; index < inputsRef.current.length; index++) {
            inputsRef.current[index].focus();
            inputsRef.current[index].value = digits[index] ?? '';
        }
    };

    const promiseRequestCode = async (
        bodyParams: URLSearchParams
    ): Promise<FetchResult> =>
        new Promise((resolve) => {
            fetch('/LoginV2/index.server.php', {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded;charset=UTF-8',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: bodyParams.toString(),
                credentials: 'include',
            })
                .then(async (response) => {
                    const data = await response.json();

                    if (data.error < 0) {
                        return resolve({
                            error: data.error,
                            type: data.type,
                            title: data.title,
                            message:
                                data.messages[0].message !== ''
                                    ? data.messages[0].message
                                    : t('error.anErrorOccurred'),
                        });
                    }

                    const result = {} as FetchResult;
                    result.error = data.error;
                    result.message = data.message;
                    result.timestamp = data.timestamp; // TODO: LoginV2 returns a timestamp that's used to validate the code expiration date. This timestamp has to be sent to the server. Server should not require this. Change this.

                    return resolve(result);
                })
                .catch(() =>
                    resolve({
                        error: -1,
                        type: 'error',
                        title: t('auth.authentication'),
                        message: t('error.anErrorOccurred'),
                    })
                )
                .finally(() => {});
        });

    const [isEmailActive, setIsEmailActive] = useState(email);

    const requestCode = async (changeMethod: boolean) => {
        if (userID <= 0 || (sms === false && email === false)) {
            return;
        }

        const bodyParams = new URLSearchParams();
        bodyParams.append('action', 'send_codigo');

        if (changeMethod) {
            bodyParams.append('metodo', !isEmailActive ? '1' : '2');
        } else {
            bodyParams.append('metodo', isEmailActive ? '1' : '2');
        }

        Object.entries(user).forEach(([el, userData]) => {
            if (userData && typeof userData === 'object') {
                Object.entries(userData).forEach(([key, value]) => {
                    bodyParams.append(`user[${el}][${key}]`, value);
                });
            } else {
                bodyParams.append(`user[${el}]`, userData as string);
            }
        });

        // call legacy server to request authentication code
        setLoading(true);
        const result: FetchResult = await promiseRequestCode(bodyParams);
        if (result.error < 0) {
            setFetchResult(result);
        } else {
            if (changeMethod) {
                setIsEmailActive(!isEmailActive);
            }
            setTimeToExpirationDate(timeToExpiration);

            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                setTimeToExpirationDate((t) => t - 1);
            }, 1000);

            inputsRef.current.forEach((input) => {
                if (input) input.value = '';
            });
        }
        setTimestamp(result.timestamp != null ? result.timestamp : 0);
        setLoading(false);
    };

    const changeShippingMethod = async () => {
        requestCode(true);
    };

    const promiseSubmitCode = async (
        bodyParams: URLSearchParams
    ): Promise<FetchResult> =>
        new Promise((resolve) => {
            fetch('/LoginV2/index.server.php', {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded;charset=UTF-8',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: bodyParams.toString(),
                credentials: 'include',
            })
                .then(async (response) => {
                    const data = await response.json();

                    const result = {} as FetchResult;
                    result.error = data.error;

                    if (result.error < 0) {
                        return resolve({
                            error: data.error,
                            type: data.type,
                            title: data.title,
                            message:
                                data.messages[0].message !== ''
                                    ? data.messages[0].message
                                    : t('error.anErrorOccurred'),
                        });
                    }

                    if (result.error === 0) {
                        // hide any visible inline toast in this component before delegating success handling
                        setOpenErrorToast(false);

                        // TODO: on success LoginV2 requires that the standard login gets called again. This is wrong. Change this
                        // this is why on success code validation, user has no session.
                        // window.location.href = '/home';
                        onSuccessCallback();
                    }

                    return resolve(result);
                })
                .catch(() =>
                    resolve({
                        error: -1,
                        type: 'error',
                        title: t('auth.authentication'),
                        message: t('error.anErrorOccurred'),
                    })
                )
                .finally(() => {});
        });

    const submitCode = async () => {
        let code = '';
        for (let index = 0; index < inputsRef.current.length; index++) {
            code += inputsRef.current[index].value;
        }

        const bodyParams = new URLSearchParams();
        bodyParams.append('action', 'submeter_codigo');
        bodyParams.append('codigo', code);
        bodyParams.append('timestamp', timestamp.toString());

        // call legacy server to validate authentication code
        setLoading(true);
        const result: FetchResult = await promiseSubmitCode(bodyParams);
        if (result.error < 0) {
            setFetchResult(result);
        }
        setLoading(false);
    };

    const handleClickSubmit = () => {
        if (timeToExpirationDate > 0) {
            submitCode();
        } else {
            requestCode(false);
        }
    };

    const effectRan = useRef(false);

    useEffect(() => {
        if (!effectRan.current) {
            requestCode(false);
            effectRan.current = true;
        }

        return () => clearInterval(intervalRef.current);
    }, []);

    const inputCodeClass =
        'input  w-full aspect-square h-auto rounded-md border-gray-500 text-center caret-transparent focus:bg-gray-100 focus:outline-none sm:h-auto sm:w-full md:h-auto md:w-full lg:h-[56px] lg:w-[56px] dark:bg-white dark:focus:bg-gray-100';

    return (
        <div className="relative flex h-full w-full items-center justify-center">
            <div className="mx-auto w-full">
                {openErrorToast && (
                    <div className="absolute bottom-full left-1/2 z-50 mb-0 w-full -translate-x-1/2 -translate-y-4">
                        <Toast
                            className="w-full"
                            type={fetchResult.type}
                            title={fetchResult.title}
                            message={fetchResult.message}
                            close={onToastClose}
                        />
                    </div>
                )}

                <div className="w-full rounded-md border-gray-50 bg-white shadow">
                    <button
                        className="inline-flex cursor-pointer px-4 pt-4 font-medium"
                        onClick={() => goBackCallback()}
                    >
                        <RiArrowLeftSLine />
                        {t('common.backToStart')}
                    </button>

                    <div className="p-4">
                        {isClientLogo ? (
                            <>
                                <img
                                    src={getClientLogoImageWithText(
                                        INSTANCE_ID
                                    )}
                                    alt=""
                                    className="h-11 w-auto"
                                />
                            </>
                        ) : (
                            <LogoWithText className="h-8 w-auto" />
                        )}
                    </div>

                    <p className="text-grey-750 px-4 pb-4 text-sm font-bold sm:text-sm md:text-base md:font-bold">
                        {isEmailActive
                            ? t('auth.validationCodeSentToEmail')
                            : t('auth.validationCodeSentToPhone')}
                    </p>

                    <p className="flex items-start gap-4 px-4 font-medium text-gray-900">
                        {t('auth.validationCode')}
                    </p>

                    {/* Start: Input Codes */}

                    <div className="flex w-full justify-between gap-2 px-4 pb-4 sm:gap-2 sm:px-4 md:gap-4 md:px-4">
                        {}
                        <input
                            id={`form2StepsCode${0}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[0] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(0, 0, 1)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                        <input
                            id={`form2StepsCode${1}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[1] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(1, 0, 2)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                        <input
                            id={`form2StepsCode${2}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[2] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(2, 1, 3)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                        <input
                            id={`form2StepsCode${3}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[3] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(3, 2, 4)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                        <input
                            id={`form2StepsCode${4}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[4] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(4, 3, 5)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                        <input
                            id={`form2StepsCode${5}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]"
                            ref={(el: HTMLInputElement) => {
                                inputsRef.current[5] = el;
                            }}
                            onInput={handleInputFilter}
                            onPaste={handlePasteEvent}
                            onKeyUp={() => handleKeyUpEvent(5, 4, 5)}
                            className={inputCodeClass}
                            required
                            maxLength={1}
                        />
                    </div>
                    {/* End: Input Codes */}

                    <p className="p-4 font-medium text-gray-900">
                        {t('auth.validationCodeValidFor')}: {minuteTimer}m{' '}
                        {secondTimer}s
                    </p>

                    <div className="p-4">
                        <button
                            type="submit"
                            className="btn-sign-in-btn-gradient size-11 w-full rounded-md outline-transparent"
                            onClick={handleClickSubmit}
                        >
                            <div className="inline-flex items-center">
                                {loading ? (
                                    <span className="loading loading-spinner loading-md"></span>
                                ) : (
                                    <>
                                        <span className="font-bold">
                                            {timeToExpirationDate > 0
                                                ? t('common.validate')
                                                : t(
                                                      'auth.resendValidationCode'
                                                  )}
                                        </span>
                                    </>
                                )}
                            </div>
                        </button>
                    </div>

                    <button
                        type="button"
                        className="w-full pb-4 pt-4 text-center font-bold text-blue-600"
                        onClick={changeShippingMethod}
                    >
                        {t('auth.changeValidationMethod')}
                    </button>

                    {isClientLogo ? (
                        <div className="mt-6 flex justify-end px-4 pb-4">
                            <LogoWithText className="h-5" />
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        </div>
    );
}
